import type { CaptureResult } from 'posthog-js';

/** Matches /cli-auth/<key> path segments (KEY is high-entropy but must not leak to analytics). */
const CLI_AUTH_PATH_SEGMENT = /\/cli-auth\/[^/?#]+/g;

/**
 * Redact CLI auth keys from URLs before analytics or logging.
 * Covers path segments (/cli-auth/KEY) and redirect query values.
 */
export const scrubCliAuthFromUrl = (url: string): string => {
  if (!url) {
    return url;
  }

  let scrubbed = url.replace(CLI_AUTH_PATH_SEGMENT, '/cli-auth/[redacted]');

  scrubbed = scrubbed.replace(/([?&]redirect=)([^&]+)/gi, (match, prefix: string, value: string) => {
    try {
      const decoded = decodeURIComponent(value.replace(/\+/g, ' '));
      if (!decoded.includes('/cli-auth/')) {
        return match;
      }
      const redacted = decoded.replace(CLI_AUTH_PATH_SEGMENT, '/cli-auth/[redacted]');
      return `${prefix}${encodeURIComponent(redacted)}`;
    } catch {
      return `${prefix}[redacted]`;
    }
  });

  return scrubbed;
};

const URL_PROPERTY_KEYS = ['$current_url', '$referrer', '$pathname'] as const;

/** Strip CLI auth secrets from PostHog autocapture URL properties. */
export const scrubCliAuthFromPostHogEvent = (
  event: CaptureResult | null
): CaptureResult | null => {
  if (!event?.properties) {
    return event;
  }

  const props = event.properties;

  for (const key of URL_PROPERTY_KEYS) {
    const value = props[key];
    if (typeof value === 'string') {
      props[key] = scrubCliAuthFromUrl(value);
    }
  }

  if (typeof props.redirect === 'string') {
    props.redirect = scrubCliAuthFromUrl(props.redirect);
  }

  return event;
};
